import { API, APIMethod, CompetitionAPI, DesignerAPI, Endpoints, Factory, LMSAPI, PageModel, ProjectAPI, TeamAPI } from '@brg/api';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ProjectsGridActions } from "../reducers/ProjectsGridSlice";
import { DesignerTab } from "../constants/designerTab";
import { EMPTY_CONTENT_TAB } from 'shared/pages/designer/components/NoProjectsContent';
import { Guard } from '@brg/toolbox';
import { ContextViewTab } from 'shared/constants/contextViewTab';
import urlApi from './urlApi';
import { getNonNullParam, mergeArraysBySubscribedId } from 'shared/components/Subscribe/support';
import { SUBSCRIBE_VISIBILITY_STATUS } from 'shared/components/Subscribe/const';
import { merge } from 'shared/helpers/utils/filters';
import { ALL_CHANNELS_TEAM_ID } from 'shared/pages/exploreHseContextsNew/const/misc';
import { ModerationType } from 'shared/pages/teams/const/ModerationTypes';
import { FilterOrManual, getFilterOrManual, sanitizeFilterForGrid } from 'shared/helpers/getFilterOrManual';
import { ITeamFilterModel } from 'shared/pages/teams/types';
import { methodParams } from './helpers/methodParams';
import { resolveCardProductTagIds } from 'shared/helpers/resolveCardProductTagIds';
import { fetchCardGetListShowcase } from 'shared/helpers/cardGetListShowcase';
import { getCardsByTeam } from 'shared/helpers/getCardsByTeam';
import { isDefineProjectOrderScopeAllowed } from 'shared/pages/teams/helpers/isDefineProjectOrderAllowed';
import { isTabOrganizationsCatalogMode } from 'shared/pages/teams/helpers/prepareCustomSortingGridFilter';
import {
  isOrganizationsEntityType,
  prepareCustomSortingGridFilter,
  buildOrganizationsGridFilterOptions,
  resolveGridEntityType,
  resolveTeamShowcaseEntityType,
  applyTabOrganizationsCatalogEmptySelectionGuard,
  applyShowcaseOrganizationsCatalogEmptySelectionGuard,
  isOuterShowcaseOrganizationsCatalogMode,
} from 'shared/pages/teams/helpers/prepareCustomSortingGridFilter';
import { getAuthorsIdsParam } from './helpers/getAuthorsIdsParam';
import { getEnableFeedParam } from 'shared/helpers/getEnableFeedParam';
import { COMPETITION_TYPE } from '_competition/const';
import ratingsApi from './metrics';
import { ShowcaseFilterType, showcaseFilterToRatingType } from 'shared/constants/ShowcaseRatings';

const PROJECTS_COUNT_FOR_OFFSET = 20;
const PROJECTS_COUNT_START_OFFSET = 0;
const PROJECTS_COUNT_LECTURE_OFFSET = 19;


type Tabs = {
  [key: string]: string[] | string | null
}

const calcOffsetValue = (page: number, count: number) => {
  return page <= count ? page : count;
}

const prepOuterFilters = (tab: any, filter: any, moderationType: unknown) => ({
  tab: tab ? { ...tab, filter: sanitizeFilterForGrid(tab.filter, moderationType) } : tab,
  filter: sanitizeFilterForGrid(filter, moderationType),
})

const getLiteRequest = (filter: any) => {
  // if (filter?.hasOwnProperty('moderationTypes') &&
  //   filter?.moderationTypes?.includes(ModerationType.Channel)) {
  //   return false
  // }
  return true
}

const appendCustomSorting = (
  finalFilter: Record<string, unknown>,
  tab: {
    customSorting?: boolean
    isProjectsView?: boolean
    entityType?: string
    customTeamsSorting?: string[]
    filter?: ITeamFilterModel
  } | undefined,
  team: {
    customSorting?: boolean
    isProjectsView?: boolean
    teamId?: string
    mainEntityType?: string
    moderationType?: unknown
    customProjectSorting?: string[]
    customTeamsSorting?: string[]
  } | undefined,
  isNoTabs: boolean,
  teamId?: string,
  moderationType?: unknown,
  entityType?: string,
  mainFilter?: ITeamFilterModel,
) => {
  const tabEntityType = entityType || tab?.entityType || team?.mainEntityType
  const resolvedEntityType = isNoTabs
    ? resolveTeamShowcaseEntityType(team, tabEntityType)
    : tabEntityType

  if (!isDefineProjectOrderScopeAllowed(
    moderationType,
    resolvedEntityType,
    undefined,
    { forTab: !isNoTabs },
  )) {
    return finalFilter;
  }

  const selectionFilter = isNoTabs ? mainFilter : tab?.filter;
  const isOuterSelectionControlled = moderationType === ModerationType.Outer;
  const hasTabCustomSorting = !isNoTabs && !!tab?.customSorting;
  const isTabOrganizationsCatalog = !isNoTabs
    && moderationType === ModerationType.Outer
    && isTabOrganizationsCatalogMode(tab, resolvedEntityType, moderationType);
  const isShowcaseOrganizationsCatalog = isNoTabs
    && isOuterShowcaseOrganizationsCatalogMode(team, resolvedEntityType);
  const canUseCustomSorting = !isOuterSelectionControlled
    || (isOuterSelectionControlled && (
      !selectionFilter?.usingFilterOrManual
      || hasTabCustomSorting
      || isShowcaseOrganizationsCatalog
      || isTabOrganizationsCatalog
    ));

  const hasShowcaseManualOrganizationsSelection = isNoTabs
    && isShowcaseOrganizationsCatalog
    && (team?.customTeamsSorting?.length ?? 0) > 0
  const hasTabManualOrganizationsSelection = moderationType === ModerationType.Outer
    && !isNoTabs
    && isTabOrganizationsCatalog
    && (tab?.customTeamsSorting?.length ?? 0) > 0
  const useCustomSorting = canUseCustomSorting
    && (
      tab?.customSorting
      || (isNoTabs && team?.customSorting)
      || hasShowcaseManualOrganizationsSelection
      || hasTabManualOrganizationsSelection
    );
  if (!useCustomSorting) {
    return finalFilter;
  }

  const resolvedTeamId = teamId || team?.teamId;
  const isOrganizations = isOrganizationsEntityType(resolvedEntityType)

  return {
    ...finalFilter,
    customSorting: true,
    ...(!isOrganizations && resolvedTeamId ? { teamId: resolvedTeamId } : {}),
  };
}

const getFinalFilter = (tab: any, filter: any, replaceParamsList?: any, isNoTabs?: boolean, separateFilters?: boolean, isResidentsPage?: boolean) => {
  const {
    path,
    filter: tabFilter,
    override,
    isOverride,
    withoutEmptyParams
  } = tab;

  if (override || isOverride) {
    return {
      ...(filter?.limit ? { limit: filter.limit } : {}),
      ...tabFilter,
    }
  }

  const hasOrFilter = !!tabFilter?.orFilter && tabFilter.orFilter.length > 0 && !isNoTabs;
  const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;

  if (separateFilters) {
    return {
      ...tabFilter,
      ...(filter?.sort ? { sort: filter.sort } : {}),
      ...(filter?.projectFilter ? { projectFilter: filter.projectFilter } : {}),
    }
  }

  if (hasOrFilter) {
    const baseFilter = merge(filter, tabFilter);
    const mergedWithOrFilter = tabFilter.orFilter.reduce((acc: any, orItem: any) => {
      return merge(acc, orItem);
    }, filter);

    const processedFilter = {
      ...baseFilter,
      orFilter: [mergedWithOrFilter],
      ...([DesignerTab.drafts, DesignerTab.draftBooks].includes(path) ? { projectFilter: 9 } : {}),
      ...(replaceParamsList)
    };

    return processedFilter;
  }

  if (hasMainFilterOrFilter) {
    const updatedOrFilter = filter.orFilter?.map((item: any) => ({
      ...item,
      projectStatus: filter?.projectStatus
    })) || [];

    const processedFilter = {
      ...filter,
      orFilter: updatedOrFilter,
      ...([DesignerTab.drafts, DesignerTab.draftBooks].includes(path) ? { projectFilter: 9 } : {}),
      ...(replaceParamsList)
    };

    return processedFilter;
  }

  const mergedFilter = {
    ...(merge(filter, tabFilter)),
    ...([DesignerTab.drafts, DesignerTab.draftBooks].includes(path) ? { projectFilter: 9 } : {}),
    ...(replaceParamsList)
  }

  if (withoutEmptyParams) {
    return getNonNullParam(mergedFilter)
  }

  return {
    ...mergedFilter,
    ...(isResidentsPage || mergedFilter?.moderationTypes?.includes(7) || mergedFilter?.moderationTypes?.includes(8) ? {} : { excludeModerationTypes: [7, 8] }),
  };

}

const fetchFeed = async (props: any, options: any = {}) => {
  const urls = new URLSearchParams();
  Object.keys(props).forEach((key) => {
    if (props[key] instanceof Array) {
      for (const val of props[key]) {
        urls.append(key, val);
      }
    } else {
      urls.append(key, props[key]);
    }
  });
  return await API.fetch(
    Guard.void(options.url, ProjectAPI.url) + '/api/project/GetSubscriptionProjects' + '?' + urls.toString(),
    options,
  );
}

const fetchMainPageList = async (props: any, options: any = {}) => {
  return await API.request()
    .endpoint(Endpoints.geograffee)
    .url('/api/project/GetMainPageList')
    .post()
    .body(props)
    .send(options);
}

const getMapProjects = Factory.createGetMethodExtended(ProjectAPI, '/api/project/GetMainPageLite');
const getCalendarProjects = Factory.createGetMethodExtended(ProjectAPI, '/api/project/GetCalendarProjects');

const methods = {
  projects: ProjectAPI.GetList,
  competition: CompetitionAPI.GetList,
  competitions_new: CompetitionAPI.GetList,
  books: ProjectAPI.GetList,
  organizations: TeamAPI.GetList,
  educationSubscribes: TeamAPI.GetList,
  modules: LMSAPI.GetModulesList,
  main: ProjectAPI.GetMainPage,
  channels: ProjectAPI.GetMainPageList,
  mainList: fetchMainPageList,
  jury: CompetitionAPI.GetList,
  feed: fetchFeed,
  designer: DesignerAPI.GetList,
  map: getMapProjects,
  newspaper: getCalendarProjects,
}

const resolveProjectFetchMethod = (
  defaultMethod: APIMethod<any, any>,
  finalFilter: Record<string, unknown>,
  entityType?: string,
): APIMethod<any, any> => {
  if (!finalFilter?.customSorting) {
    return defaultMethod;
  }

  if (entityType === 'organizations') {
    return methods.organizations;
  }

  return methods.projects;
}

let abortController: AbortController | null = null;

export const fetchProjectsGrid = createAsyncThunk(
  'projects/fetchProjectsGrid',
  async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const {
      filter,
      channelOwnerId,
      accountId,
      currentCategory,
      currentTab,
      contextName,
      teachersOnly,
      teamId,
      isProfile,
      isPeoplePage,
      moderationType,
      isCompetitions: isCompetitionsProp,
      isLMSHSE,
      isMe,
      isSchool,
      isLectureMedia,
      designerId,
      isHseSpaceDesignerPage,
      team,
      isResidentsPage,
      hash,
      productCards
    } = args;

    const gridContextKey = args.isChannel && teamId ? teamId : contextName;

    const { tabs, ratingsFilter } = getState().projectsGrid;

    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    const { signal } = abortController;

    dispatch(ProjectsGridActions.setTabLoading({ currentCategory, currentTab, value: true }));
    dispatch(ProjectsGridActions.setPrevProductCardsFilter(productCards))

    const mainTab = tabs[currentCategory].find(({ path }: any) => path === currentTab);

    const isLmsCtx = mainTab?.filter?.contexts?.includes('hsecourses');

    const isDefaultEmptyTab = currentTab === EMPTY_CONTENT_TAB && !isLmsCtx;
    const isNoTabs = currentTab === "all" && Object.values(tabs).length === 1;

    const isCompetitions = isCompetitionsProp || mainTab?.entityType === 'competitions_new';

    const hasOrFilter = !!mainTab?.filter?.orFilter && mainTab.filter.orFilter.length > 0 && !isNoTabs;
    const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;

    const paramsForReplace = {
      ...(teachersOnly ? {
        globalTypes: ['Cooperator', 'Employee'],
      } : {}),
      ...(isHseSpaceDesignerPage ? {
        globalTypes: [...mainTab.filter.globalTypes, 'hsecreativestudent'],
        contexts: ['organizations', 'manager_units'].includes(mainTab?.name)? [] : ['hsespace', 'hsedesign', 'spaceunits']
      } : {}),

    }

    const { tab: sanitizedMainTab, filter: sanitizedFilter } = prepOuterFilters(mainTab, filter, moderationType)

    const requestType = getFilterOrManual(moderationType, (sanitizedMainTab?.filter || sanitizedFilter) as ITeamFilterModel)
    const separateFilters = moderationType && moderationType === ModerationType.Outer
    const organizationsGridFilterOptions = buildOrganizationsGridFilterOptions(
      contextName,
      sanitizedFilter?.contexts,
      team,
    )
    const resolvedEntityType = resolveGridEntityType(
      team,
      sanitizedMainTab?.entityType || team?.mainEntityType,
      !isNoTabs,
    )

    const finalFilter = prepareCustomSortingGridFilter(
      appendCustomSorting(
        applyShowcaseOrganizationsCatalogEmptySelectionGuard(
          applyTabOrganizationsCatalogEmptySelectionGuard(
            getFinalFilter(
              sanitizedMainTab,
              sanitizedFilter,
              paramsForReplace,
              isNoTabs,
              separateFilters,
              isResidentsPage
            ),
            {
              tab: sanitizedMainTab,
              team,
              entityType: resolvedEntityType,
              moderationType,
              isTabRequest: !isNoTabs,
            },
          ),
          {
            team,
            entityType: resolvedEntityType,
            moderationType,
            isNoTabs,
          },
        ),
        sanitizedMainTab,
        team,
        isNoTabs,
        teamId,
        moderationType,
        resolvedEntityType,
        sanitizedFilter as unknown as ITeamFilterModel,
      ),
      resolvedEntityType,
      organizationsGridFilterOptions,
    );

    const mainMethod: APIMethod<any, any> =
      ['people_in_detail', 'people', 'statuses']?.includes(resolvedEntityType || '') ? methods.designer :
        (isDefaultEmptyTab) ? ((hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : methods.main)
          : isCompetitions ? methods['competition']
            //@ts-ignore
            : (hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : (methods[resolvedEntityType] || methods.main);

    const { hasAnnotation } = finalFilter;

    const noEmptyChannelParam = teamId === ALL_CHANNELS_TEAM_ID ? { channelNotEmpty: true } : {}
    const authorIdsFromTeamIdsParam = getAuthorsIdsParam(
      moderationType,
      teamId,
      sanitizedMainTab ?? mainTab,
      resolvedEntityType,
    );
    const onlyBooksParam = isSchool || isLMSHSE || isPeoplePage || isProfile || isCompetitions ? {} : !finalFilter.hasOwnProperty('onlyBooks') ? { onlyBooks: false } : {};
    const isLiteParam = { isLite: getLiteRequest(mainTab?.filter) }
    const enableFeedParam = getEnableFeedParam(teamId, mainTab)

    const commonParams = {
      currentTab,
      currentCategory,
      path: mainTab.path,
      usingFilterOrManual: mainTab?.filter?.usingFilterOrManual,
      entityType: resolvedEntityType,
      isDrafts: mainTab.isDrafts,
      contextName: gridContextKey,
      hasAnnotation,
      hash,
    }



    if (['competitions', 'practices']?.includes(mainTab?.entityType)) {
      await ProjectAPI.GetParticipateCompetitions({
        isPractice: mainTab?.entityType === 'practices',
      })
        .then((d) => d.json())
        .then((d) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: d,
            paging: {
              pages: 1,
              page: 1,
              count: d.totalProjectsCount,
            },
            ...commonParams,
            isFinalPage: true,
          }))
        });
    } else if (['projects_ratings', 'authors_ratings']?.includes(mainTab?.entityType)) {
      const dataKey = mainTab?.entityType === 'projects_ratings' ? 'projectModel' : 'designerModel';
      const maxProjectsForView = 20;
      const response = await ratingsApi().fetchRatingsByPeriod(mainTab?.entityType,{
        limit: /*finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET*/maxProjectsForView,
        page: /*finalFilter?.page ||*/ 1,
        date: ratingsFilter?.date,
        ratingType: showcaseFilterToRatingType[ratingsFilter?.type ?? ShowcaseFilterType.views],
        intervalType: ratingsFilter?.intervalType,
      }) as Promise<{ data: any[], paging: { page: number, pages: number, count: number } }>

      const normalizedData = response?.data?.map((d: any) => ({...d?.[dataKey]}))

      dispatch(ProjectsGridActions.setSingleTabData({
        data: normalizedData,
        paging: response?.paging,
        isFinalPage: /*response?.paging?.pages === response?.paging?.page*/ true,
        ...commonParams,
      }))
    }
   else if (mainTab?.entityType === 'newspaper') {
      await mainMethod({
        limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
        offset: PROJECTS_COUNT_START_OFFSET,
        calendarType: mainTab?.entitySubtype
      }, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            ...commonParams,
            isDrafts: false,
            isFinalPage: paging.pages === paging.page,
          }))
        })
    } else if (mainTab?.entityType === 'modules') {

      await LMSAPI.GetModulesList({
        teamId: teamId,
        ...(mainTab?.path && mainTab.path !== 'all' && { lMSModuleDisciplines: mainTab.path }),
        limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
      }, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            ...commonParams,
            // @ts-ignore
            isFinalPage: paging?.pages === paging?.page,
          }))
        })
    } else if (resolvedEntityType === 'organizations' && !isCompetitions && !isProfile && isLMSHSE && !isPeoplePage) {
      const fetchMethod = resolveProjectFetchMethod(mainMethod, finalFilter, resolvedEntityType);

      await fetchMethod({
        ...finalFilter,
        ...(finalFilter?.sort ? { sort: finalFilter.sort } : {}),
        hasAnnotation: false,
        offset: PROJECTS_COUNT_START_OFFSET
      }, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            ...commonParams,
            isFinalPage: paging.pages === paging.page,
          }))
        })
    } else if (mainTab?.entityType === DesignerTab.subscriptionsProjects) {
      await methods.feed({
        accountId: accountId,
        limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
        ...(!!filter?.sort ? { sort: filter.sort } : {}),
        ...(!!filter?.tagIds ? { tagIds: filter.tagIds } : {}),
        ...(!!filter.hasProductCards ? { hasProductCards: filter.hasProductCards } : {})
      }, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            ...commonParams,
            isDrafts: false,
            isFinalPage: paging.pages === paging.page,
          }))
        })
    } else if (mainTab?.entityType === DesignerTab.subscriptions) {
      const visibilityOpt = `${!isMe ? `&visibility=${SUBSCRIBE_VISIBILITY_STATUS.visible}` : ''}`;
      const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
      const subscriberType = '0'
      const targetType = 'userProjects'
      const params = `subscriberId=${accountId}&subscriberType=${subscriberType}&targetType=${targetType}&limit=${offset}${visibilityOpt} `
      const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscriptions?${params}`

      await API.fetch(url, { signal })
        .then((r) => r.json())
        .then(async (subscriptions) => {
          const { data } = subscriptions;
          const subsIds = data?.map((i: any) => i?.targetId);

          if (!subsIds?.length) return;

          await DesignerAPI.GetList({
            designerIds: subsIds,
            offset: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
          }).then((r) => r.json())
            .then((r) => {
              dispatch(ProjectsGridActions.setSingleTabData({
                // @ts-ignore
                data: mergeArraysBySubscribedId(r?.data, data),
                paging: r?.paging,
                ...commonParams,
                entityType: 'subscriptions',
                isFinalPage: false,
              }))
            })
        })

    } else if (mainTab?.entityType === DesignerTab.subscriptionsTeams) {
      const visibilityOpt = `${!isMe ? `&visibility=${SUBSCRIBE_VISIBILITY_STATUS.visible}` : ''}`;
      const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
      const subscriberType = '0'
      const targetType = 'team'
      const params = `subscriberId=${accountId}&subscriberType=${subscriberType}&targetType=${targetType}&limit=${offset}${visibilityOpt} `
      const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscriptions?${params}`
      const sort = finalFilter?.sort || finalFilter?.projectFilter || 1

      await API.fetch(url, { signal })
        .then((r) => r.json())
        .then(async (subscriptions) => {
          const { data: subscriptionsData, paging } = subscriptions;
          const teamIds = subscriptionsData?.map((i: any) => i?.targetId);
          if (!teamIds?.length) return;

          await TeamAPI.GetList({
            teamIds,
            offset: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
            sort,
          }).then((r) => r.json())
            .then((teamsResponse: any) => {
              const teamsData = teamsResponse?.data || [];
              const subscriptionByTeamId = new Map(
                subscriptionsData.map((sub: any) => [sub.targetId, sub])
              );
              const mergedTeams = teamsData
                .map((team: any) => {
                  const sub = subscriptionByTeamId.get(team.teamId);

                  if (!sub) return null;
      
                  return {
                    ...team,
                    subscribeStatus: sub?.status,
                    visibility: sub?.visibility,
                  };
                })
                .filter(Boolean);

              dispatch(ProjectsGridActions.setSingleTabData({
                data: mergedTeams,
                paging: paging || teamsResponse?.paging,
                ...commonParams,
                entityType: DesignerTab.subscriptionsTeams,
                isFinalPage: paging?.pages === paging.page,
              }))
            })
        })
    } else if (mainTab?.entityType === DesignerTab.subscribers) {
      const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
      const targetType = 'userProjects'
      const params = `targetId=${accountId}&targetType=${targetType}&limit=${offset}`
      const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscribers?${params}`

      await API.fetch(url, { signal })
        .then((r) => r.json())
        .then(async (subscriptions) => {
          const { data, paging } = subscriptions;
          const subsIds = data?.map((i: any) => i?.subscriberId);

          if (!subsIds?.length) return;

          await DesignerAPI.GetList({
            designerIds: subsIds,
            offset: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
          }).then((r) => r.json())
            .then((r) => {
              dispatch(ProjectsGridActions.setSingleTabData({
                // @ts-ignore
                data: mergeArraysBySubscribedId(r?.data, data),
                paging: r?.paging,
                ...commonParams,
                entityType: 'subscribers',
                isFinalPage: paging?.pages === paging.page,
              }))
            })
        })
    } else if (currentTab === 'discount') {
      await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/GetDesignerDiscountCards')
        .addQuery('designerId', designerId)
        .get()
        .send({ signal })
        .then((r) => r.json())
        .then((cards) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: cards,
            paging: { count: 20, page: 1, pages: 0 },
            ...commonParams,
            isFinalPage: true,
          }))
        })
    } else if (mainTab?.entityType === DesignerTab.competitionCerts) {
      await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/GetCompetitionCerts')
        .addQuery('designerId', designerId)
        .get()
        .send({ signal })
        .then((r) => r.json())
        .then((cards) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: cards,
            paging: { count: 20, page: 1, pages: 0 },
            ...commonParams,
            isFinalPage: false,
          }))
        })
    } else if (
      isPeoplePage && [
        ContextViewTab.teachers,
        ContextViewTab.students,
        ContextViewTab.alumnus
      ]?.includes(currentTab)
    ) {
      const {
        limit,
        globalTypes,
        isLite,
        sort,
        isContextIgnored,
        appNameIgnore
      } = mainTab?.filter;
      await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/GetList')
        .addQuery('limit', limit)
        .addQuery('globalTypes', isSchool && currentTab === ContextViewTab.students ? ['Hseyerevanstudent'] : globalTypes)
        .addQuery('sort', sort)
        .addQuery('isContextIgnored', false)
        .addQuery('appNameIgnore', appNameIgnore)
        .addQuery('isLite', isLite)
        .get()
        .send({ signal })
        .then((r) => r.json())
        .then((r) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: r?.data,
            paging: r?.paging,
            ...commonParams,
            isFinalPage: false,
          }))
        })
    } else if (mainTab?.entityType === 'content_type_for_display') {
      const limit = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET;
      const tagIds = resolveCardProductTagIds(mainTab, finalFilter, team);

      if (!teamId) {
        dispatch(ProjectsGridActions.setSingleTabData({
          data: [],
          paging: { page: 1, pages: 1, count: 0 },
          ...commonParams,
          isFinalPage: true,
        }))
      } else {
        await fetchCardGetListShowcase(1, limit, tagIds, { signal })
          .then((raw: any) => {
            const data = raw?.data ?? raw?.Data;
            const paging = raw?.paging ?? raw?.Paging;
            dispatch(ProjectsGridActions.setSingleTabData({
              data,
              paging,
              ...commonParams,
              isFinalPage: paging?.pages === paging?.page,
            }))
          })
      }
    }  
    else if (mainTab?.entityType === 'products') {
      const limit = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET;
      const sort = finalFilter?.sort || 1

      if (!teamId) {
        dispatch(ProjectsGridActions.setSingleTabData({
          data: [],
          paging: { page: 1, pages: 1, count: 0 },
          ...commonParams,
          isFinalPage: true,
        }))
      } else {
        await getCardsByTeam(1, limit, teamId, sort, { signal })
          .then((raw: any) => {
            const data = raw?.data ?? raw?.Data;
            const paging = raw?.paging ?? raw?.Paging;
            dispatch(ProjectsGridActions.setSingleTabData({
              data,
              paging,
              ...commonParams,
              isFinalPage: paging?.pages === paging?.page,
            }))
          })
      }
    } 
    else if (mainTab?.entityType === 'broadcast') {
      dispatch(ProjectsGridActions.setSingleTabData({
        data: [],
        paging: { page: 1, pages: 1, count: 0 },
        ...commonParams,
        entityType: 'broadcast',
        isFinalPage: true,
      }))
    }
    else {
      const fetchMethod = resolveProjectFetchMethod(mainMethod, finalFilter, resolvedEntityType);
      const requestParams = methodParams(requestType, {
        filter: finalFilter,
        onlyBooksParam,
        noEmptyChannelParam,
        authorIdsFromTeamIdsParam,
        enableFeedParam,
        isLiteParam,
        channelOwnerId,
        moderationType,
        entityType: resolvedEntityType,
        defineProjectOrderForTab: !isNoTabs,
        offset: PROJECTS_COUNT_START_OFFSET,
        isLite: false,
        sort: filter?.sort,
      });

      await fetchMethod(requestParams, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            ...commonParams,
            isFinalPage: paging.pages === paging.page,
          }))
        })
    }
  }
);

export const fetchProjectsGridMore = createAsyncThunk(
  'projects/fetchProjectsGridMore',
  async (args: any, { getState }: { getState: any }) => {
    const { currentTab, currentCategory, filter, moderationType, accountId, teachersOnly, isLMSHSE, isProfile, isCompetitions, teamId, isMe, isMedia, isPeoplePage, isSchool, contextName, isHseSpaceDesignerPage, team, isResidentsPage } = args;
    const { tabs, data, ratingsFilter } = getState().projectsGrid;

    const tab = tabs[currentCategory].find(({ path }: any) => path === currentTab);
    const { paging }: any = data[currentCategory][currentTab];

    const paramsForReplace = {
      ...(teachersOnly ? {
        globalTypes: ['Cooperator', 'Employee'],
      } : {}),
      ...(isSchool && currentTab === ContextViewTab.students ? {
        globalTypes: ['Hseyerevanstudent'],
      } : {}),
      ...(isHseSpaceDesignerPage ? {
        globalTypes: [...tab.filter.globalTypes, 'hsecreativestudent'],
        //todo рызобраться, почему это здесь, а не в фильтре таба
        contexts: ['hsespace', 'hsedesign', 'spaceunits']
      } : {}),
    }
    const isLiteParam = { isLite: getLiteRequest(tab?.filter) }
    const isNoTabs = currentTab === "all" && Object.values(tabs).length === 1;
    const hasOrFilter = !!tab?.filter?.orFilter && tab.filter.orFilter.length > 0 && !isNoTabs;
    const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;
    const { tab: sanitizedTab, filter: sanitizedFilter } = prepOuterFilters(tab, filter, moderationType)
    const requestType = getFilterOrManual(moderationType, (sanitizedTab?.filter || sanitizedFilter) as ITeamFilterModel)
    const separateFilters = moderationType && moderationType === ModerationType.Outer
    const organizationsGridFilterOptions = buildOrganizationsGridFilterOptions(
      contextName,
      sanitizedFilter?.contexts,
      team,
    )
    const resolvedEntityType = resolveGridEntityType(
      team,
      sanitizedTab?.entityType || team?.mainEntityType,
      !isNoTabs,
    )
    const authorIdsFromTeamIdsParam = getAuthorsIdsParam(
      moderationType,
      teamId,
      sanitizedTab,
      resolvedEntityType,
    )

    const finalFilter = prepareCustomSortingGridFilter(
      appendCustomSorting(
        applyTabOrganizationsCatalogEmptySelectionGuard(
          getFinalFilter(
            sanitizedTab,
            sanitizedFilter,
            paramsForReplace,
            isNoTabs,
            separateFilters,
            isResidentsPage,
          ),
          {
            tab: sanitizedTab,
            team,
            entityType: resolvedEntityType,
            moderationType,
            isTabRequest: !isNoTabs,
          },
        ),
        sanitizedTab,
        team,
        isNoTabs,
        teamId,
        moderationType,
        resolvedEntityType,
        sanitizedFilter as unknown as ITeamFilterModel,
      ),
      resolvedEntityType,
      organizationsGridFilterOptions,
    );

    const onlyBooksParam = isSchool || isLMSHSE || isPeoplePage || isProfile || isCompetitions ? {} : !finalFilter.hasOwnProperty('onlyBooks') ? { onlyBooks: false } : {};
    const enableFeedParam = getEnableFeedParam(teamId, tab)

    const defaultMethod: APIMethod<any, any> =
      tab?.entityType === DesignerTab.subscriptionsProjects ? methods.feed :
        isPeoplePage || ['people_in_detail', 'people', 'statuses']?.includes(resolvedEntityType || '') ?
          methods.designer :
          //@ts-ignore
          (hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : (methods[resolvedEntityType] || methods.main);

    const method = resolveProjectFetchMethod(defaultMethod, finalFilter, resolvedEntityType);

    if (tab?.entityType === DesignerTab.subscriptions) {
      const json = await urlApi()?.fetchSubscriptions({
        subscriberId: accountId,
        page: paging.page + 1,
        offset: paging.offset + filter.limit,
        limit: PROJECTS_COUNT_FOR_OFFSET,
        tab,
        isMe,
      })

      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: json.paging,
        data: json.data,
        //@ts-ignore
        isFinalPage: json.paging.pages === json?.paging?.page
      };
    }
    if (['projects_ratings', 'authors_ratings']?.includes(tab?.entityType)) {
      const maxProjectsForView = 20;
      const response = await ratingsApi().fetchRatingsByPeriod(tab?.entityType,{
        limit: /*finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET*/maxProjectsForView,
        page: /*paging?.page + 1*/1,
        date: ratingsFilter?.date,
        ratingType: showcaseFilterToRatingType[ratingsFilter?.type ?? ShowcaseFilterType.views],
        intervalType: ratingsFilter?.intervalType,
      }) as Promise<{ data: any[], paging: { page: number, pages: number, count: number } }>
  
      const dataKey = tab?.entityType === 'projects_ratings' ? 'projectModel' : 'designerModel';
      const normalizedData = response?.data?.map((d: any) => ({...d?.[dataKey]}))
      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: response?.paging,
        data: normalizedData,
        isFinalPage: /*response?.paging?.pages === response?.paging?.page*/ true,
      }
    }

    if (tab?.entityType === DesignerTab.subscribers) {
      const json = await urlApi()?.fetchSubscribers({
        subscriberId: accountId,
        page: paging.page + 1,
        offset: paging.offset + filter.limit,
        limit: PROJECTS_COUNT_FOR_OFFSET,
        tab,
      })

      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: json.paging,
        data: json.data,
        //@ts-ignore
        isFinalPage: json.paging.pages === json?.paging?.page
      };
    }

    if (tab?.entityType === DesignerTab.subscriptionsTeams) {
      const visibilityOpt = `${!isMe ? `&visibility=${SUBSCRIBE_VISIBILITY_STATUS.visible}` : ''}`;
      const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
      const subscriberType = '0'
      const targetType = 'team';
      const params = `subscriberId=${accountId}&subscriberType=${subscriberType}&targetType=${targetType}&limit=${offset}&page=${paging.page + 1}${visibilityOpt}`
      const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscriptions?${params}`
      const sort = finalFilter?.sort || finalFilter?.projectFilter || 1
      const subscriptions = await API.fetch(url, {}).then((r) => r.json());
      const { data: subscriptionsData, paging: subscriptionsPaging } = subscriptions;
      const teamIds = subscriptionsData?.map((i: any) => i?.targetId);

      if (!teamIds?.length) {
        return {
          currentTab,
          currentCategory,
          entityType: tab.entityType,
          paging: subscriptionsPaging,
          data: [],
          //@ts-ignore
          isFinalPage: subscriptionsPaging?.pages === subscriptionsPaging?.page
        };
      }

      const teams = await TeamAPI.GetList({ teamIds, offset, sort }).then((r) => r.json());
      const teamsData = teams?.data || [];
      const subscriptionByTeamId = new Map(
        subscriptionsData.map((sub: any) => [sub.targetId, sub])
      );
      const mergedTeams = teamsData
        .map((team: any) => {
          const sub = subscriptionByTeamId.get(team.teamId);

          if (!sub) return null;

          return {
            ...team,
            subscribeStatus: sub?.status,
            visibility: sub?.visibility,
          };
        })
        .filter(Boolean);

      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: subscriptionsPaging || teams?.paging,
        data: mergedTeams,
        //@ts-ignore
        isFinalPage: subscriptionsPaging?.pages === subscriptionsPaging?.page
      };
    }

    if (tab?.entityType === 'newspaper') {
      const json = await method({
        offset: paging.offset + filter.limit,
        page: paging.page + 1,
        limit: finalFilter?.limit,
        calendarType: tab?.entitySubtype
      }).then((d) => d.json());

      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: json.paging,
        data: json.data,
        usingFilterOrManual: tab?.filter?.usingFilterOrManual,
      };
    }

    if (tab?.entityType === 'content_type_for_display') {
      const limit = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET;
      const tagIds = resolveCardProductTagIds(tab, finalFilter, team);

      if (!teamId) {
        return {
          currentTab,
          currentCategory,
          entityType: tab.entityType,
          paging,
          data: [],
          usingFilterOrManual: tab?.filter?.usingFilterOrManual,
          //@ts-ignore
          isFinalPage: true,
        };
      }

      const json = await fetchCardGetListShowcase(paging.page + 1, limit, tagIds);

      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: json.paging,
        data: json.data,
        usingFilterOrManual: tab?.filter?.usingFilterOrManual,
        //@ts-ignore
        isFinalPage: json.paging?.pages <= json?.paging?.page,
      };
    }

    if (tab?.entityType === 'products') {
      const limit = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET;
      const sort = finalFilter?.sort || 1

      if (!teamId) {
        return {
          currentTab,
          currentCategory,
          entityType: tab.entityType,
          paging,
          data: [],
          //@ts-ignore
          isFinalPage: true,
        };
      }

      const json = await getCardsByTeam(paging.page + 1, limit, teamId, sort)

      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: json.paging,
        data: json.data,
        //@ts-ignore
        isFinalPage: json.paging?.pages <= json?.paging?.page,
      };
    }

    if (tab?.entityType === 'broadcast') {
      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: { page: 1, pages: 1, count: 0 },
        data: [],
        isFinalPage: true,
      };
    }



    const json = await API.Json(
      method(methodParams(requestType, {
        ...finalFilter,
        filter: finalFilter,
        onlyBooksParam,
        enableFeedParam,
        authorIdsFromTeamIdsParam,
        isLiteParam,
        moderationType,
        entityType: resolvedEntityType,
        defineProjectOrderForTab: !isNoTabs,
        isLite: false,
        offset: paging.offset + filter.limit,
        page: paging.page + 1,
        ...(tab?.entityType === DesignerTab.subscriptionsProjects ? { accountId } : {}),
        ...(tab?.withoutEmptyParams ? {} : filter?.sort ? { sort: filter?.sort } : {})
      })),
      // method({
      //   ...finalFilter,
      //   ...onlyBooksParam,
      //   isLite: false,
      //   offset: paging.offset + filter.limit,
      //   page: paging.page + 1,
      //   ...(usingManual ? {
      //     ...replaceIdsParams(finalFilter?.projectIds),
      //     projectStatus: [1,10],
      //   } : {}),
      //   ...(tab?.entityType === DesignerTab.subscriptionsProjects ? { accountId } : {}),
      //   ...(tab?.withoutEmptyParams ? {} : filter?.sort ? { sort: filter?.sort } : {})
      // }),
    )

    return {
      currentTab,
      currentCategory,
      entityType: tab.entityType,
      paging: json.paging,
      data: json.data,
      usingFilterOrManual: tab?.filter?.usingFilterOrManual,
      //@ts-ignore
      isFinalPage: json.paging.pages <= json?.paging?.page
    };
  }
);

export const fetchInfiniteProjectsWithUpdate = createAsyncThunk(
  'projects/fetchInfiniteProjectsWithUpdate',
  async (args: any, { getState }: { getState: any }) => {
    const { page, lang } = args;
    const { filter, paging } = getState().projects;

    const json = await API.Json(
      ProjectAPI.GetMainPage({
        ...filter,
        offset: calcOffsetValue(
          (Number(page) * PROJECTS_COUNT_FOR_OFFSET
          ),
          paging?.count
        ) - PROJECTS_COUNT_FOR_OFFSET
      }),
    )
    return lang ? (API.lang({
      paging: json.paging,
      data: [...json.data],
      //@ts-ignore
      isFinalPage: json.paging.pages <= json?.paging?.page
    }, lang) as any) : {
      paging: json.paging,
      data: [...json.data],
      //@ts-ignore
      isFinalPage: json.paging.pages <= json?.paging?.page
    };
  }
);
import { API, APIMethod, CompetitionAPI, DesignerAPI, Endpoints, Factory, LMSAPI, PageModel, ProjectAPI, TeamAPI } from '@brg/api';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ProjectsGridActions } from "../reducers/ProjectsGridSlice";
import { DesignerTab } from "../constants/designerTab";
import { EMPTY_CONTENT_TAB } from 'shared/pages/designer/components/NoProjectsContent';
import { Guard } from '@brg/toolbox';
import { ContextViewTab } from 'shared/constants/contextViewTab';
import urlApi from './urlApi';
import { getNonNullParam, mergeArraysBySubscribedId } from 'shared/components/Subscribe/support';
import { SUBSCRIBE_VISIBILITY_STATUS } from 'shared/components/Subscribe/const';
import { merge } from 'shared/helpers/utils/filters';
import { ALL_CHANNELS_TEAM_ID } from 'shared/pages/exploreHseContextsNew/const/misc';
import { ModerationType } from 'shared/pages/teams/const/ModerationTypes';
import { FilterOrManual, getFilterOrManual, sanitizeFilterForGrid } from 'shared/helpers/getFilterOrManual';
import { ITeamFilterModel } from 'shared/pages/teams/types';
import { methodParams } from './helpers/methodParams';
import { resolveCardProductTagIds } from 'shared/helpers/resolveCardProductTagIds';
import { fetchCardGetListShowcase } from 'shared/helpers/cardGetListShowcase';
import { getCardsByTeam } from 'shared/helpers/getCardsByTeam';
import { isDefineProjectOrderScopeAllowed } from 'shared/pages/teams/helpers/isDefineProjectOrderAllowed';
import { isTabOrganizationsCatalogMode } from 'shared/pages/teams/helpers/prepareCustomSortingGridFilter';
import {
  isOrganizationsEntityType,
  prepareCustomSortingGridFilter,
  buildOrganizationsGridFilterOptions,
  resolveGridEntityType,
  resolveTeamShowcaseEntityType,
  applyTabOrganizationsCatalogEmptySelectionGuard,
  applyShowcaseOrganizationsCatalogEmptySelectionGuard,
  isOuterShowcaseOrganizationsCatalogMode,
} from 'shared/pages/teams/helpers/prepareCustomSortingGridFilter';
import { getAuthorsIdsParam } from './helpers/getAuthorsIdsParam';
import { getEnableFeedParam } from 'shared/helpers/getEnableFeedParam';
import { COMPETITION_TYPE } from '_competition/const';
import ratingsApi from './metrics';
import { ShowcaseFilterType, showcaseFilterToRatingType } from 'shared/constants/ShowcaseRatings';

const PROJECTS_COUNT_FOR_OFFSET = 20;
const PROJECTS_COUNT_START_OFFSET = 0;
const PROJECTS_COUNT_LECTURE_OFFSET = 19;


type Tabs = {
  [key: string]: string[] | string | null
}

const calcOffsetValue = (page: number, count: number) => {
  return page <= count ? page : count;
}

const prepOuterFilters = (tab: any, filter: any, moderationType: unknown) => ({
  tab: tab ? { ...tab, filter: sanitizeFilterForGrid(tab.filter, moderationType) } : tab,
  filter: sanitizeFilterForGrid(filter, moderationType),
})

const getLiteRequest = (filter: any) => {
  // if (filter?.hasOwnProperty('moderationTypes') &&
  //   filter?.moderationTypes?.includes(ModerationType.Channel)) {
  //   return false
  // }
  return true
}

const appendCustomSorting = (
  finalFilter: Record<string, unknown>,
  tab: {
    customSorting?: boolean
    isProjectsView?: boolean
    entityType?: string
    customTeamsSorting?: string[]
    filter?: ITeamFilterModel
  } | undefined,
  team: {
    customSorting?: boolean
    isProjectsView?: boolean
    teamId?: string
    mainEntityType?: string
    moderationType?: unknown
    customProjectSorting?: string[]
    customTeamsSorting?: string[]
  } | undefined,
  isNoTabs: boolean,
  teamId?: string,
  moderationType?: unknown,
  entityType?: string,
  mainFilter?: ITeamFilterModel,
) => {
  const tabEntityType = entityType || tab?.entityType || team?.mainEntityType
  const resolvedEntityType = isNoTabs
    ? resolveTeamShowcaseEntityType(team, tabEntityType)
    : tabEntityType

  if (!isDefineProjectOrderScopeAllowed(
    moderationType,
    resolvedEntityType,
    undefined,
    { forTab: !isNoTabs },
  )) {
    return finalFilter;
  }

  const selectionFilter = isNoTabs ? mainFilter : tab?.filter;
  const isOuterSelectionControlled = moderationType === ModerationType.Outer;
  const hasTabCustomSorting = !isNoTabs && !!tab?.customSorting;
  const isTabOrganizationsCatalog = !isNoTabs
    && moderationType === ModerationType.Outer
    && isTabOrganizationsCatalogMode(tab, resolvedEntityType, moderationType);
  const isShowcaseOrganizationsCatalog = isNoTabs
    && isOuterShowcaseOrganizationsCatalogMode(team, resolvedEntityType);
  const canUseCustomSorting = !isOuterSelectionControlled
    || (isOuterSelectionControlled && (
      !selectionFilter?.usingFilterOrManual
      || hasTabCustomSorting
      || isShowcaseOrganizationsCatalog
      || isTabOrganizationsCatalog
    ));

  const hasShowcaseManualOrganizationsSelection = isNoTabs
    && isShowcaseOrganizationsCatalog
    && (team?.customTeamsSorting?.length ?? 0) > 0
  const hasTabManualOrganizationsSelection = moderationType === ModerationType.Outer
    && !isNoTabs
    && isTabOrganizationsCatalog
    && (tab?.customTeamsSorting?.length ?? 0) > 0
  const useCustomSorting = canUseCustomSorting
    && (
      tab?.customSorting
      || (isNoTabs && team?.customSorting)
      || hasShowcaseManualOrganizationsSelection
      || hasTabManualOrganizationsSelection
    );
  if (!useCustomSorting) {
    return finalFilter;
  }

  const resolvedTeamId = teamId || team?.teamId;
  const isOrganizations = isOrganizationsEntityType(resolvedEntityType)

  return {
    ...finalFilter,
    customSorting: true,
    ...(!isOrganizations && resolvedTeamId ? { teamId: resolvedTeamId } : {}),
  };
}

const getFinalFilter = (tab: any, filter: any, replaceParamsList?: any, isNoTabs?: boolean, separateFilters?: boolean, isResidentsPage?: boolean) => {
  const {
    path,
    filter: tabFilter,
    override,
    isOverride,
    withoutEmptyParams
  } = tab;

  if (override || isOverride) {
    return {
      ...(filter?.limit ? { limit: filter.limit } : {}),
      ...tabFilter,
    }
  }

  const hasOrFilter = !!tabFilter?.orFilter && tabFilter.orFilter.length > 0 && !isNoTabs;
  const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;

  if (separateFilters) {
    return {
      ...tabFilter,
      ...(filter?.sort ? { sort: filter.sort } : {}),
      ...(filter?.projectFilter ? { projectFilter: filter.projectFilter } : {}),
    }
  }

  if (hasOrFilter) {
    const baseFilter = merge(filter, tabFilter);
    const mergedWithOrFilter = tabFilter.orFilter.reduce((acc: any, orItem: any) => {
      return merge(acc, orItem);
    }, filter);

    const processedFilter = {
      ...baseFilter,
      orFilter: [mergedWithOrFilter],
      ...([DesignerTab.drafts, DesignerTab.draftBooks].includes(path) ? { projectFilter: 9 } : {}),
      ...(replaceParamsList)
    };

    return processedFilter;
  }

  if (hasMainFilterOrFilter) {
    const updatedOrFilter = filter.orFilter?.map((item: any) => ({
      ...item,
      projectStatus: filter?.projectStatus
    })) || [];

    const processedFilter = {
      ...filter,
      orFilter: updatedOrFilter,
      ...([DesignerTab.drafts, DesignerTab.draftBooks].includes(path) ? { projectFilter: 9 } : {}),
      ...(replaceParamsList)
    };

    return processedFilter;
  }

  const mergedFilter = {
    ...(merge(filter, tabFilter)),
    ...([DesignerTab.drafts, DesignerTab.draftBooks].includes(path) ? { projectFilter: 9 } : {}),
    ...(replaceParamsList)
  }

  if (withoutEmptyParams) {
    return getNonNullParam(mergedFilter)
  }

  return {
    ...mergedFilter,
    ...(isResidentsPage || mergedFilter?.moderationTypes?.includes(7) || mergedFilter?.moderationTypes?.includes(8) ? {} : { excludeModerationTypes: [7, 8] }),
  };

}

const fetchFeed = async (props: any, options: any = {}) => {
  const urls = new URLSearchParams();
  Object.keys(props).forEach((key) => {
    if (props[key] instanceof Array) {
      for (const val of props[key]) {
        urls.append(key, val);
      }
    } else {
      urls.append(key, props[key]);
    }
  });
  return await API.fetch(
    Guard.void(options.url, ProjectAPI.url) + '/api/project/GetSubscriptionProjects' + '?' + urls.toString(),
    options,
  );
}

const fetchMainPageList = async (props: any, options: any = {}) => {
  return await API.request()
    .endpoint(Endpoints.geograffee)
    .url('/api/project/GetMainPageList')
    .post()
    .body(props)
    .send(options);
}

const getMapProjects = Factory.createGetMethodExtended(ProjectAPI, '/api/project/GetMainPageLite');
const getCalendarProjects = Factory.createGetMethodExtended(ProjectAPI, '/api/project/GetCalendarProjects');

const methods = {
  projects: ProjectAPI.GetList,
  competition: CompetitionAPI.GetList,
  competitions_new: CompetitionAPI.GetList,
  books: ProjectAPI.GetList,
  organizations: TeamAPI.GetList,
  educationSubscribes: TeamAPI.GetList,
  modules: LMSAPI.GetModulesList,
  main: ProjectAPI.GetMainPage,
  channels: ProjectAPI.GetMainPageList,
  mainList: fetchMainPageList,
  jury: CompetitionAPI.GetList,
  feed: fetchFeed,
  designer: DesignerAPI.GetList,
  map: getMapProjects,
  newspaper: getCalendarProjects,
}

const resolveProjectFetchMethod = (
  defaultMethod: APIMethod<any, any>,
  finalFilter: Record<string, unknown>,
  entityType?: string,
): APIMethod<any, any> => {
  if (!finalFilter?.customSorting) {
    return defaultMethod;
  }

  if (entityType === 'organizations') {
    return methods.organizations;
  }

  return methods.projects;
}

let abortController: AbortController | null = null;

export const fetchProjectsGrid = createAsyncThunk(
  'projects/fetchProjectsGrid',
  async (args: any, { getState, dispatch }: { getState: any, dispatch: any }) => {
    const {
      filter,
      channelOwnerId,
      accountId,
      currentCategory,
      currentTab,
      contextName,
      teachersOnly,
      teamId,
      isProfile,
      isPeoplePage,
      moderationType,
      isCompetitions: isCompetitionsProp,
      isLMSHSE,
      isMe,
      isSchool,
      isLectureMedia,
      designerId,
      isHseSpaceDesignerPage,
      team,
      isResidentsPage,
      hash,
      productCards
    } = args;

    const gridContextKey = args.isChannel && teamId ? teamId : contextName;

    const { tabs, ratingsFilter } = getState().projectsGrid;

    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    const { signal } = abortController;

    dispatch(ProjectsGridActions.setTabLoading({ currentCategory, currentTab, value: true }));
    dispatch(ProjectsGridActions.setPrevProductCardsFilter(productCards))

    const mainTab = tabs[currentCategory].find(({ path }: any) => path === currentTab);

    const isLmsCtx = mainTab?.filter?.contexts?.includes('hsecourses');

    const isDefaultEmptyTab = currentTab === EMPTY_CONTENT_TAB && !isLmsCtx;
    const isNoTabs = currentTab === "all" && Object.values(tabs).length === 1;

    const isCompetitions = isCompetitionsProp || mainTab?.entityType === 'competitions_new';

    const hasOrFilter = !!mainTab?.filter?.orFilter && mainTab.filter.orFilter.length > 0 && !isNoTabs;
    const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;

    const paramsForReplace = {
      ...(teachersOnly ? {
        globalTypes: ['Cooperator', 'Employee'],
      } : {}),
      ...(isHseSpaceDesignerPage ? {
        globalTypes: [...mainTab.filter.globalTypes, 'hsecreativestudent'],
        contexts: ['organizations', 'manager_units'].includes(mainTab?.name)? [] : ['hsespace', 'hsedesign', 'spaceunits']
      } : {}),

    }

    const { tab: sanitizedMainTab, filter: sanitizedFilter } = prepOuterFilters(mainTab, filter, moderationType)

    const requestType = getFilterOrManual(moderationType, (sanitizedMainTab?.filter || sanitizedFilter) as ITeamFilterModel)
    const separateFilters = moderationType && moderationType === ModerationType.Outer
    const organizationsGridFilterOptions = buildOrganizationsGridFilterOptions(
      contextName,
      sanitizedFilter?.contexts,
      team,
    )
    const resolvedEntityType = resolveGridEntityType(
      team,
      sanitizedMainTab?.entityType || team?.mainEntityType,
      !isNoTabs,
    )

    const finalFilter = prepareCustomSortingGridFilter(
      appendCustomSorting(
        applyShowcaseOrganizationsCatalogEmptySelectionGuard(
          applyTabOrganizationsCatalogEmptySelectionGuard(
            getFinalFilter(
              sanitizedMainTab,
              sanitizedFilter,
              paramsForReplace,
              isNoTabs,
              separateFilters,
              isResidentsPage
            ),
            {
              tab: sanitizedMainTab,
              team,
              entityType: resolvedEntityType,
              moderationType,
              isTabRequest: !isNoTabs,
            },
          ),
          {
            team,
            entityType: resolvedEntityType,
            moderationType,
            isNoTabs,
          },
        ),
        sanitizedMainTab,
        team,
        isNoTabs,
        teamId,
        moderationType,
        resolvedEntityType,
        sanitizedFilter as unknown as ITeamFilterModel,
      ),
      resolvedEntityType,
      organizationsGridFilterOptions,
    );

    const mainMethod: APIMethod<any, any> =
      ['people_in_detail', 'people', 'statuses']?.includes(resolvedEntityType || '') ? methods.designer :
        (isDefaultEmptyTab) ? ((hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : methods.main)
          : isCompetitions ? methods['competition']
            //@ts-ignore
            : (hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : (methods[resolvedEntityType] || methods.main);

    const { hasAnnotation } = finalFilter;

    const noEmptyChannelParam = teamId === ALL_CHANNELS_TEAM_ID ? { channelNotEmpty: true } : {}
    const authorIdsFromTeamIdsParam = getAuthorsIdsParam(
      moderationType,
      teamId,
      sanitizedMainTab ?? mainTab,
      resolvedEntityType,
    );
    const onlyBooksParam = isSchool || isLMSHSE || isPeoplePage || isProfile || isCompetitions ? {} : !finalFilter.hasOwnProperty('onlyBooks') ? { onlyBooks: false } : {};
    const isLiteParam = { isLite: getLiteRequest(mainTab?.filter) }
    const enableFeedParam = getEnableFeedParam(teamId, mainTab)

    const commonParams = {
      currentTab,
      currentCategory,
      path: mainTab.path,
      usingFilterOrManual: mainTab?.filter?.usingFilterOrManual,
      entityType: resolvedEntityType,
      isDrafts: mainTab.isDrafts,
      contextName: gridContextKey,
      hasAnnotation,
      hash,
    }



    if (['competitions', 'practices']?.includes(mainTab?.entityType)) {
      await ProjectAPI.GetParticipateCompetitions({
        isPractice: mainTab?.entityType === 'practices',
      })
        .then((d) => d.json())
        .then((d) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: d,
            paging: {
              pages: 1,
              page: 1,
              count: d.totalProjectsCount,
            },
            ...commonParams,
            isFinalPage: true,
          }))
        });
    } else if (['projects_ratings', 'authors_ratings']?.includes(mainTab?.entityType)) {
      const dataKey = mainTab?.entityType === 'projects_ratings' ? 'projectModel' : 'designerModel';
      const maxProjectsForView = 20;
      const response = await ratingsApi().fetchRatingsByPeriod(mainTab?.entityType,{
        limit: /*finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET*/maxProjectsForView,
        page: /*finalFilter?.page ||*/ 1,
        date: ratingsFilter?.date,
        ratingType: showcaseFilterToRatingType[ratingsFilter?.type ?? ShowcaseFilterType.views],
        intervalType: ratingsFilter?.intervalType,
      }) as Promise<{ data: any[], paging: { page: number, pages: number, count: number } }>

      const normalizedData = response?.data?.map((d: any) => ({...d?.[dataKey]}))

      dispatch(ProjectsGridActions.setSingleTabData({
        data: normalizedData,
        paging: response?.paging,
        isFinalPage: /*response?.paging?.pages === response?.paging?.page*/ true,
        ...commonParams,
      }))
    }
   else if (mainTab?.entityType === 'newspaper') {
      await mainMethod({
        limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
        offset: PROJECTS_COUNT_START_OFFSET,
        calendarType: mainTab?.entitySubtype
      }, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            ...commonParams,
            isDrafts: false,
            isFinalPage: paging.pages === paging.page,
          }))
        })
    } else if (mainTab?.entityType === 'modules') {

      await LMSAPI.GetModulesList({
        teamId: teamId,
        ...(mainTab?.path && mainTab.path !== 'all' && { lMSModuleDisciplines: mainTab.path }),
        limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
      }, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            ...commonParams,
            // @ts-ignore
            isFinalPage: paging?.pages === paging?.page,
          }))
        })
    } else if (resolvedEntityType === 'organizations' && !isCompetitions && !isProfile && isLMSHSE && !isPeoplePage) {
      const fetchMethod = resolveProjectFetchMethod(mainMethod, finalFilter, resolvedEntityType);

      await fetchMethod({
        ...finalFilter,
        ...(finalFilter?.sort ? { sort: finalFilter.sort } : {}),
        hasAnnotation: false,
        offset: PROJECTS_COUNT_START_OFFSET
      }, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            ...commonParams,
            isFinalPage: paging.pages === paging.page,
          }))
        })
    } else if (mainTab?.entityType === DesignerTab.subscriptionsProjects) {
      await methods.feed({
        accountId: accountId,
        limit: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
        ...(!!filter?.sort ? { sort: filter.sort } : {}),
        ...(!!filter?.tagIds ? { tagIds: filter.tagIds } : {}),
        ...(!!filter.hasProductCards ? { hasProductCards: filter.hasProductCards } : {})
      }, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            ...commonParams,
            isDrafts: false,
            isFinalPage: paging.pages === paging.page,
          }))
        })
    } else if (mainTab?.entityType === DesignerTab.subscriptions) {
      const visibilityOpt = `${!isMe ? `&visibility=${SUBSCRIBE_VISIBILITY_STATUS.visible}` : ''}`;
      const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
      const subscriberType = '0'
      const targetType = 'userProjects'
      const params = `subscriberId=${accountId}&subscriberType=${subscriberType}&targetType=${targetType}&limit=${offset}${visibilityOpt} `
      const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscriptions?${params}`

      await API.fetch(url, { signal })
        .then((r) => r.json())
        .then(async (subscriptions) => {
          const { data } = subscriptions;
          const subsIds = data?.map((i: any) => i?.targetId);

          if (!subsIds?.length) return;

          await DesignerAPI.GetList({
            designerIds: subsIds,
            offset: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
          }).then((r) => r.json())
            .then((r) => {
              dispatch(ProjectsGridActions.setSingleTabData({
                // @ts-ignore
                data: mergeArraysBySubscribedId(r?.data, data),
                paging: r?.paging,
                ...commonParams,
                entityType: 'subscriptions',
                isFinalPage: false,
              }))
            })
        })

    } else if (mainTab?.entityType === DesignerTab.subscriptionsTeams) {
      const visibilityOpt = `${!isMe ? `&visibility=${SUBSCRIBE_VISIBILITY_STATUS.visible}` : ''}`;
      const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
      const subscriberType = '0'
      const targetType = 'team'
      const params = `subscriberId=${accountId}&subscriberType=${subscriberType}&targetType=${targetType}&limit=${offset}${visibilityOpt} `
      const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscriptions?${params}`
      const sort = finalFilter?.sort || finalFilter?.projectFilter || 1

      await API.fetch(url, { signal })
        .then((r) => r.json())
        .then(async (subscriptions) => {
          const { data: subscriptionsData, paging } = subscriptions;
          const teamIds = subscriptionsData?.map((i: any) => i?.targetId);
          if (!teamIds?.length) return;

          await TeamAPI.GetList({
            teamIds,
            offset: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET,
            sort,
          }).then((r) => r.json())
            .then((teamsResponse: any) => {
              const teamsData = teamsResponse?.data || [];
              const subscriptionByTeamId = new Map(
                subscriptionsData.map((sub: any) => [sub.targetId, sub])
              );
              const mergedTeams = teamsData
                .map((team: any) => {
                  const sub = subscriptionByTeamId.get(team.teamId);

                  if (!sub) return null;
      
                  return {
                    ...team,
                    subscribeStatus: sub?.status,
                    visibility: sub?.visibility,
                  };
                })
                .filter(Boolean);

              dispatch(ProjectsGridActions.setSingleTabData({
                data: mergedTeams,
                paging: paging || teamsResponse?.paging,
                ...commonParams,
                entityType: DesignerTab.subscriptionsTeams,
                isFinalPage: paging?.pages === paging.page,
              }))
            })
        })
    } else if (mainTab?.entityType === DesignerTab.subscribers) {
      const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
      const targetType = 'userProjects'
      const params = `targetId=${accountId}&targetType=${targetType}&limit=${offset}`
      const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscribers?${params}`

      await API.fetch(url, { signal })
        .then((r) => r.json())
        .then(async (subscriptions) => {
          const { data, paging } = subscriptions;
          const subsIds = data?.map((i: any) => i?.subscriberId);

          if (!subsIds?.length) return;

          await DesignerAPI.GetList({
            designerIds: subsIds,
            offset: finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
          }).then((r) => r.json())
            .then((r) => {
              dispatch(ProjectsGridActions.setSingleTabData({
                // @ts-ignore
                data: mergeArraysBySubscribedId(r?.data, data),
                paging: r?.paging,
                ...commonParams,
                entityType: 'subscribers',
                isFinalPage: paging?.pages === paging.page,
              }))
            })
        })
    } else if (currentTab === 'discount') {
      await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/GetDesignerDiscountCards')
        .addQuery('designerId', designerId)
        .get()
        .send({ signal })
        .then((r) => r.json())
        .then((cards) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: cards,
            paging: { count: 20, page: 1, pages: 0 },
            ...commonParams,
            isFinalPage: true,
          }))
        })
    } else if (mainTab?.entityType === DesignerTab.competitionCerts) {
      await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/GetCompetitionCerts')
        .addQuery('designerId', designerId)
        .get()
        .send({ signal })
        .then((r) => r.json())
        .then((cards) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: cards,
            paging: { count: 20, page: 1, pages: 0 },
            ...commonParams,
            isFinalPage: false,
          }))
        })
    } else if (
      isPeoplePage && [
        ContextViewTab.teachers,
        ContextViewTab.students,
        ContextViewTab.alumnus
      ]?.includes(currentTab)
    ) {
      const {
        limit,
        globalTypes,
        isLite,
        sort,
        isContextIgnored,
        appNameIgnore
      } = mainTab?.filter;
      await API.request()
        .endpoint(Endpoints.geograffee)
        .url('/api/Designer/GetList')
        .addQuery('limit', limit)
        .addQuery('globalTypes', isSchool && currentTab === ContextViewTab.students ? ['Hseyerevanstudent'] : globalTypes)
        .addQuery('sort', sort)
        .addQuery('isContextIgnored', false)
        .addQuery('appNameIgnore', appNameIgnore)
        .addQuery('isLite', isLite)
        .get()
        .send({ signal })
        .then((r) => r.json())
        .then((r) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data: r?.data,
            paging: r?.paging,
            ...commonParams,
            isFinalPage: false,
          }))
        })
    } else if (mainTab?.entityType === 'content_type_for_display') {
      const limit = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET;
      const tagIds = resolveCardProductTagIds(mainTab, finalFilter, team);

      if (!teamId) {
        dispatch(ProjectsGridActions.setSingleTabData({
          data: [],
          paging: { page: 1, pages: 1, count: 0 },
          ...commonParams,
          isFinalPage: true,
        }))
      } else {
        await fetchCardGetListShowcase(1, limit, tagIds, { signal })
          .then((raw: any) => {
            const data = raw?.data ?? raw?.Data;
            const paging = raw?.paging ?? raw?.Paging;
            dispatch(ProjectsGridActions.setSingleTabData({
              data,
              paging,
              ...commonParams,
              isFinalPage: paging?.pages === paging?.page,
            }))
          })
      }
    }  
    else if (mainTab?.entityType === 'products') {
      const limit = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET;
      const sort = finalFilter?.sort || 1

      if (!teamId) {
        dispatch(ProjectsGridActions.setSingleTabData({
          data: [],
          paging: { page: 1, pages: 1, count: 0 },
          ...commonParams,
          isFinalPage: true,
        }))
      } else {
        await getCardsByTeam(1, limit, teamId, sort, { signal })
          .then((raw: any) => {
            const data = raw?.data ?? raw?.Data;
            const paging = raw?.paging ?? raw?.Paging;
            dispatch(ProjectsGridActions.setSingleTabData({
              data,
              paging,
              ...commonParams,
              isFinalPage: paging?.pages === paging?.page,
            }))
          })
      }
    } 
    else if (mainTab?.entityType === 'broadcast') {
      dispatch(ProjectsGridActions.setSingleTabData({
        data: [],
        paging: { page: 1, pages: 1, count: 0 },
        ...commonParams,
        entityType: 'broadcast',
        isFinalPage: true,
      }))
    }
    else {
      const fetchMethod = resolveProjectFetchMethod(mainMethod, finalFilter, resolvedEntityType);
      const requestParams = methodParams(requestType, {
        filter: finalFilter,
        onlyBooksParam,
        noEmptyChannelParam,
        authorIdsFromTeamIdsParam,
        enableFeedParam,
        isLiteParam,
        channelOwnerId,
        moderationType,
        entityType: resolvedEntityType,
        defineProjectOrderForTab: !isNoTabs,
        offset: PROJECTS_COUNT_START_OFFSET,
        isLite: false,
        sort: filter?.sort,
      });

      await fetchMethod(requestParams, { signal })
        .then((d) => d.json())
        .then(({ data, paging }) => {
          dispatch(ProjectsGridActions.setSingleTabData({
            data,
            paging,
            ...commonParams,
            isFinalPage: paging.pages === paging.page,
          }))
        })
    }
  }
);

export const fetchProjectsGridMore = createAsyncThunk(
  'projects/fetchProjectsGridMore',
  async (args: any, { getState }: { getState: any }) => {
    const { currentTab, currentCategory, filter, moderationType, accountId, teachersOnly, isLMSHSE, isProfile, isCompetitions, teamId, isMe, isMedia, isPeoplePage, isSchool, contextName, isHseSpaceDesignerPage, team, isResidentsPage } = args;
    const { tabs, data, ratingsFilter } = getState().projectsGrid;

    const tab = tabs[currentCategory].find(({ path }: any) => path === currentTab);
    const { paging }: any = data[currentCategory][currentTab];

    const paramsForReplace = {
      ...(teachersOnly ? {
        globalTypes: ['Cooperator', 'Employee'],
      } : {}),
      ...(isSchool && currentTab === ContextViewTab.students ? {
        globalTypes: ['Hseyerevanstudent'],
      } : {}),
      ...(isHseSpaceDesignerPage ? {
        globalTypes: [...tab.filter.globalTypes, 'hsecreativestudent'],
        //todo рызобраться, почему это здесь, а не в фильтре таба
        contexts: ['hsespace', 'hsedesign', 'spaceunits']
      } : {}),
    }
    const isLiteParam = { isLite: getLiteRequest(tab?.filter) }
    const isNoTabs = currentTab === "all" && Object.values(tabs).length === 1;
    const hasOrFilter = !!tab?.filter?.orFilter && tab.filter.orFilter.length > 0 && !isNoTabs;
    const hasMainFilterOrFilter = !!filter?.orFilter && filter.orFilter.length > 0;
    const { tab: sanitizedTab, filter: sanitizedFilter } = prepOuterFilters(tab, filter, moderationType)
    const requestType = getFilterOrManual(moderationType, (sanitizedTab?.filter || sanitizedFilter) as ITeamFilterModel)
    const separateFilters = moderationType && moderationType === ModerationType.Outer
    const organizationsGridFilterOptions = buildOrganizationsGridFilterOptions(
      contextName,
      sanitizedFilter?.contexts,
      team,
    )
    const resolvedEntityType = resolveGridEntityType(
      team,
      sanitizedTab?.entityType || team?.mainEntityType,
      !isNoTabs,
    )
    const authorIdsFromTeamIdsParam = getAuthorsIdsParam(
      moderationType,
      teamId,
      sanitizedTab,
      resolvedEntityType,
    )

    const finalFilter = prepareCustomSortingGridFilter(
      appendCustomSorting(
        applyTabOrganizationsCatalogEmptySelectionGuard(
          getFinalFilter(
            sanitizedTab,
            sanitizedFilter,
            paramsForReplace,
            isNoTabs,
            separateFilters,
            isResidentsPage,
          ),
          {
            tab: sanitizedTab,
            team,
            entityType: resolvedEntityType,
            moderationType,
            isTabRequest: !isNoTabs,
          },
        ),
        sanitizedTab,
        team,
        isNoTabs,
        teamId,
        moderationType,
        resolvedEntityType,
        sanitizedFilter as unknown as ITeamFilterModel,
      ),
      resolvedEntityType,
      organizationsGridFilterOptions,
    );

    const onlyBooksParam = isSchool || isLMSHSE || isPeoplePage || isProfile || isCompetitions ? {} : !finalFilter.hasOwnProperty('onlyBooks') ? { onlyBooks: false } : {};
    const enableFeedParam = getEnableFeedParam(teamId, tab)

    const defaultMethod: APIMethod<any, any> =
      tab?.entityType === DesignerTab.subscriptionsProjects ? methods.feed :
        isPeoplePage || ['people_in_detail', 'people', 'statuses']?.includes(resolvedEntityType || '') ?
          methods.designer :
          //@ts-ignore
          (hasOrFilter || hasMainFilterOrFilter) ? methods.mainList : (methods[resolvedEntityType] || methods.main);

    const method = resolveProjectFetchMethod(defaultMethod, finalFilter, resolvedEntityType);

    if (tab?.entityType === DesignerTab.subscriptions) {
      const json = await urlApi()?.fetchSubscriptions({
        subscriberId: accountId,
        page: paging.page + 1,
        offset: paging.offset + filter.limit,
        limit: PROJECTS_COUNT_FOR_OFFSET,
        tab,
        isMe,
      })

      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: json.paging,
        data: json.data,
        //@ts-ignore
        isFinalPage: json.paging.pages === json?.paging?.page
      };
    }
    if (['projects_ratings', 'authors_ratings']?.includes(tab?.entityType)) {
      const maxProjectsForView = 20;
      const response = await ratingsApi().fetchRatingsByPeriod(tab?.entityType,{
        limit: /*finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET*/maxProjectsForView,
        page: /*paging?.page + 1*/1,
        date: ratingsFilter?.date,
        ratingType: showcaseFilterToRatingType[ratingsFilter?.type ?? ShowcaseFilterType.views],
        intervalType: ratingsFilter?.intervalType,
      }) as Promise<{ data: any[], paging: { page: number, pages: number, count: number } }>
  
      const dataKey = tab?.entityType === 'projects_ratings' ? 'projectModel' : 'designerModel';
      const normalizedData = response?.data?.map((d: any) => ({...d?.[dataKey]}))
      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: response?.paging,
        data: normalizedData,
        isFinalPage: /*response?.paging?.pages === response?.paging?.page*/ true,
      }
    }

    if (tab?.entityType === DesignerTab.subscribers) {
      const json = await urlApi()?.fetchSubscribers({
        subscriberId: accountId,
        page: paging.page + 1,
        offset: paging.offset + filter.limit,
        limit: PROJECTS_COUNT_FOR_OFFSET,
        tab,
      })

      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: json.paging,
        data: json.data,
        //@ts-ignore
        isFinalPage: json.paging.pages === json?.paging?.page
      };
    }

    if (tab?.entityType === DesignerTab.subscriptionsTeams) {
      const visibilityOpt = `${!isMe ? `&visibility=${SUBSCRIBE_VISIBILITY_STATUS.visible}` : ''}`;
      const offset = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET
      const subscriberType = '0'
      const targetType = 'team';
      const params = `subscriberId=${accountId}&subscriberType=${subscriberType}&targetType=${targetType}&limit=${offset}&page=${paging.page + 1}${visibilityOpt}`
      const url = `${Endpoints.subscriptions}/api/Subscription/GetSubscriptions?${params}`
      const sort = finalFilter?.sort || finalFilter?.projectFilter || 1
      const subscriptions = await API.fetch(url, {}).then((r) => r.json());
      const { data: subscriptionsData, paging: subscriptionsPaging } = subscriptions;
      const teamIds = subscriptionsData?.map((i: any) => i?.targetId);

      if (!teamIds?.length) {
        return {
          currentTab,
          currentCategory,
          entityType: tab.entityType,
          paging: subscriptionsPaging,
          data: [],
          //@ts-ignore
          isFinalPage: subscriptionsPaging?.pages === subscriptionsPaging?.page
        };
      }

      const teams = await TeamAPI.GetList({ teamIds, offset, sort }).then((r) => r.json());
      const teamsData = teams?.data || [];
      const subscriptionByTeamId = new Map(
        subscriptionsData.map((sub: any) => [sub.targetId, sub])
      );
      const mergedTeams = teamsData
        .map((team: any) => {
          const sub = subscriptionByTeamId.get(team.teamId);

          if (!sub) return null;

          return {
            ...team,
            subscribeStatus: sub?.status,
            visibility: sub?.visibility,
          };
        })
        .filter(Boolean);

      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: subscriptionsPaging || teams?.paging,
        data: mergedTeams,
        //@ts-ignore
        isFinalPage: subscriptionsPaging?.pages === subscriptionsPaging?.page
      };
    }

    if (tab?.entityType === 'newspaper') {
      const json = await method({
        offset: paging.offset + filter.limit,
        page: paging.page + 1,
        limit: finalFilter?.limit,
        calendarType: tab?.entitySubtype
      }).then((d) => d.json());

      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: json.paging,
        data: json.data,
        usingFilterOrManual: tab?.filter?.usingFilterOrManual,
      };
    }

    if (tab?.entityType === 'content_type_for_display') {
      const limit = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET;
      const tagIds = resolveCardProductTagIds(tab, finalFilter, team);

      if (!teamId) {
        return {
          currentTab,
          currentCategory,
          entityType: tab.entityType,
          paging,
          data: [],
          usingFilterOrManual: tab?.filter?.usingFilterOrManual,
          //@ts-ignore
          isFinalPage: true,
        };
      }

      const json = await fetchCardGetListShowcase(paging.page + 1, limit, tagIds);

      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: json.paging,
        data: json.data,
        usingFilterOrManual: tab?.filter?.usingFilterOrManual,
        //@ts-ignore
        isFinalPage: json.paging?.pages <= json?.paging?.page,
      };
    }

    if (tab?.entityType === 'products') {
      const limit = finalFilter?.limit || PROJECTS_COUNT_FOR_OFFSET;
      const sort = finalFilter?.sort || 1

      if (!teamId) {
        return {
          currentTab,
          currentCategory,
          entityType: tab.entityType,
          paging,
          data: [],
          //@ts-ignore
          isFinalPage: true,
        };
      }

      const json = await getCardsByTeam(paging.page + 1, limit, teamId, sort)

      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: json.paging,
        data: json.data,
        //@ts-ignore
        isFinalPage: json.paging?.pages <= json?.paging?.page,
      };
    }

    if (tab?.entityType === 'broadcast') {
      return {
        currentTab,
        currentCategory,
        entityType: tab.entityType,
        paging: { page: 1, pages: 1, count: 0 },
        data: [],
        isFinalPage: true,
      };
    }



    const json = await API.Json(
      method(methodParams(requestType, {
        ...finalFilter,
        filter: finalFilter,
        onlyBooksParam,
        enableFeedParam,
        authorIdsFromTeamIdsParam,
        isLiteParam,
        moderationType,
        entityType: resolvedEntityType,
        defineProjectOrderForTab: !isNoTabs,
        isLite: false,
        offset: paging.offset + filter.limit,
        page: paging.page + 1,
        ...(tab?.entityType === DesignerTab.subscriptionsProjects ? { accountId } : {}),
        ...(tab?.withoutEmptyParams ? {} : filter?.sort ? { sort: filter?.sort } : {})
      })),
      // method({
      //   ...finalFilter,
      //   ...onlyBooksParam,
      //   isLite: false,
      //   offset: paging.offset + filter.limit,
      //   page: paging.page + 1,
      //   ...(usingManual ? {
      //     ...replaceIdsParams(finalFilter?.projectIds),
      //     projectStatus: [1,10],
      //   } : {}),
      //   ...(tab?.entityType === DesignerTab.subscriptionsProjects ? { accountId } : {}),
      //   ...(tab?.withoutEmptyParams ? {} : filter?.sort ? { sort: filter?.sort } : {})
      // }),
    )

    return {
      currentTab,
      currentCategory,
      entityType: tab.entityType,
      paging: json.paging,
      data: json.data,
      usingFilterOrManual: tab?.filter?.usingFilterOrManual,
      //@ts-ignore
      isFinalPage: json.paging.pages <= json?.paging?.page
    };
  }
);

export const fetchInfiniteProjectsWithUpdate = createAsyncThunk(
  'projects/fetchInfiniteProjectsWithUpdate',
  async (args: any, { getState }: { getState: any }) => {
    const { page, lang } = args;
    const { filter, paging } = getState().projects;

    const json = await API.Json(
      ProjectAPI.GetMainPage({
        ...filter,
        offset: calcOffsetValue(
          (Number(page) * PROJECTS_COUNT_FOR_OFFSET
          ),
          paging?.count
        ) - PROJECTS_COUNT_FOR_OFFSET
      }),
    )
    return lang ? (API.lang({
      paging: json.paging,
      data: [...json.data],
      //@ts-ignore
      isFinalPage: json.paging.pages <= json?.paging?.page
    }, lang) as any) : {
      paging: json.paging,
      data: [...json.data],
      //@ts-ignore
      isFinalPage: json.paging.pages <= json?.paging?.page
    };
  }
);
